import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender, Header } from "@tanstack/react-table";
import { ArrowUpDown, Pin, PinOff } from "lucide-react";
import { CSSProperties, useCallback, useMemo } from "react";

interface TanstackTableThProps<TData, TValue> {
  header: Header<TData, TValue>;
  isPinable?: boolean;
  isResizable?: boolean;
  pinSize?: number;
}

export const TanstackTableTh = <TData, TValue>({
  header,
  isPinable = false,
  isResizable = false,
  pinSize = 12,
}: TanstackTableThProps<TData, TValue>) => {
  const { column } = header;
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: column.id,
  });

  const isSortable = column.getCanSort();
  const isPinned = column.getIsPinned();
  const canPin = isPinable && column.getCanPin();
  const canResize = isResizable && column.getCanResize();

  const isLastLeftPinnedColumn = isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn = isPinned === "right" && column.getIsFirstColumn("right");

  const style: CSSProperties = useMemo(
    () => ({
      boxShadow: isLastLeftPinnedColumn
        ? "-4px 0 4px -4px inset"
        : isFirstRightPinnedColumn
          ? "4px 0 4px -4px inset"
          : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
      opacity: isDragging || isPinned ? 0.9 : 1,
      position: isPinned ? "sticky" : "relative",
      transform: CSS.Translate.toString(transform),
      width: `calc(var(--header-${header.id}-size) * 1px)`,
      zIndex: isDragging || isPinned ? 3 : 2,
    }),
    [
      isPinned,
      isDragging,
      transform,
      column,
      header.id,
      isLastLeftPinnedColumn,
      isFirstRightPinnedColumn,
    ]
  );

  const handleToggleSort = useCallback(
    () => column.toggleSorting(column.getIsSorted() === "asc"),
    [column]
  );
  const handlePinLeft = useCallback(() => column.pin("left"), [column]);
  const handleUnpin = useCallback(() => column.pin(false), [column]);
  const handleResetSize = useCallback(() => column.resetSize(), [column]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative grid h-full grid-cols-[auto_1fr_auto] items-center rounded-l-md bg-background align-middle font-medium text-muted-foreground"
    >
      <div className="flex items-center justify-start pl-1">
        {isSortable && (
          <Button
            variant="ghost"
            aria-label="Сортировать"
            title="Сортировать"
            className="h-8 p-1 2xl:h-12"
            onClick={handleToggleSort}
          >
            <ArrowUpDown size={16} />
          </Button>
        )}
      </div>

      <span
        className="flex min-w-0 cursor-grab items-center justify-center text-center text-xs 2xl:text-sm"
        {...attributes}
        {...listeners}
      >
        <span className="truncate">
          {header.isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())}
        </span>
      </span>

      <div className="flex items-center justify-end pr-1">
        {canPin && (
          <>
            {isPinned !== "left" && (
              <Button
                variant="ghost"
                aria-label="Закрепить колонку слева"
                title="Закрепить колонку слева"
                className="h-8 p-1 text-xs 2xl:h-12 2xl:text-sm"
                onClick={handlePinLeft}
              >
                <Pin size={pinSize} />
              </Button>
            )}
            {isPinned && (
              <Button
                variant="ghost"
                aria-label="Открепить колонку"
                title="Открепить колонку"
                className="h-8 p-1 text-primary text-xs 2xl:h-12 2xl:text-sm"
                onClick={handleUnpin}
              >
                <PinOff size={pinSize} radius={50} />
              </Button>
            )}
          </>
        )}
      </div>

      {canResize && (
        <div
          role="separator"
          aria-orientation="vertical"
          onDoubleClick={handleResetSize}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={cn(
            "absolute right-0 top-1/2 h-[30%] w-0.5 -translate-y-1/2 cursor-col-resize touch-none select-none hover:h-full hover:w-1",
            column.getIsResizing() ? "bg-primary opacity-100" : "bg-muted"
          )}
        />
      )}
    </div>
  );
};
